/**
 * Income Statement Chart Component
 * Displays line chart visualization of income statement data across quarters
 */

import React, { useMemo } from 'react'
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
	ChartOptions,
} from 'chart.js'
import { Line } from 'react-chartjs-2'
import type { IncomeStatement } from '../types/financials.types'
import { formatCurrency } from '../utils/formatting'

// Register Chart.js components
ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend)

export interface IncomeStatementChartProps {
	incomeStatement: IncomeStatement
	selectedLineItemIds?: string[] // Optional: filter which line items to show
	/**
	 * The chart's box. Defaults to the Financials tab's fixed 400px; a
	 * presentation slide passes 'h-full' to fill its region (MYD-503).
	 */
	className?: string
	/**
	 * Oldest quarter on the left, as time reads on a slide (MYD-503). Off, the
	 * newest is on the left, matching the Financials grid beside it.
	 */
	oldestFirst?: boolean
}

// Color palette for different line items
const COLORS = [
	'rgb(59, 130, 246)', // blue
	'rgb(16, 185, 129)', // green
	'rgb(245, 158, 11)', // amber
	'rgb(239, 68, 68)', // red
	'rgb(139, 92, 246)', // purple
	'rgb(236, 72, 153)', // pink
	'rgb(14, 165, 233)', // sky
	'rgb(34, 197, 94)', // green
	'rgb(251, 146, 60)', // orange
	'rgb(168, 85, 247)', // violet
]

export const IncomeStatementChart: React.FC<IncomeStatementChartProps> = ({
	incomeStatement,
	selectedLineItemIds,
	className = 'h-[400px]',
	oldestFirst = false,
}) => {
	const chartData = useMemo(() => {
		const { lineItems, quarters } = incomeStatement

		// Sort quarters by order (most recent first to match grid)
		const sortedQuarters = [...quarters].sort((a, b) => (oldestFirst ? a.order - b.order : b.order - a.order))

		// Filter line items if selection provided
		const itemsToShow = selectedLineItemIds
			? lineItems.filter(item => selectedLineItemIds.includes(item.id))
			: lineItems

		// Sort line items by order
		const sortedItems = [...itemsToShow].sort((a, b) => a.order - b.order)

		return {
			labels: sortedQuarters.map(q => q.label),
			datasets: sortedItems.map((lineItem, index) => {
				const color = COLORS[index % COLORS.length]

				return {
					label: lineItem.label,
					data: sortedQuarters.map(quarter => {
						const value = lineItem.values.find(v => v.quarter === quarter.id)
						return value ? value.value : 0
					}),
					borderColor: color,
					backgroundColor: color.replace('rgb', 'rgba').replace(')', ', 0.1)'),
					borderWidth: 2,
					tension: 0.3, // Smooth curves
					pointRadius: 4,
					pointHoverRadius: 6,
				}
			}),
		}
	}, [incomeStatement, selectedLineItemIds, oldestFirst])

	const options: ChartOptions<'line'> = {
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: {
				position: 'bottom' as const,
				labels: {
					padding: 15,
					usePointStyle: true,
					font: {
						size: 12,
					},
				},
			},
			title: {
				display: false,
			},
			tooltip: {
				mode: 'index',
				intersect: false,
				callbacks: {
					label: function (context) {
						let label = context.dataset.label || ''
						if (label) {
							label += ': '
						}
						if (context.parsed.y !== null) {
							label += formatCurrency(context.parsed.y)
						}
						return label
					},
				},
			},
		},
		scales: {
			y: {
				beginAtZero: true,
				ticks: {
					callback: function (value) {
						return formatCurrency(Number(value), { maximumFractionDigits: 0 })
					},
				},
				grid: {
					color: 'rgba(0, 0, 0, 0.05)',
				},
			},
			x: {
				grid: {
					display: false,
				},
			},
		},
		interaction: {
			mode: 'nearest',
			axis: 'x',
			intersect: false,
		},
	}

	return (
		<div className={`w-full ${className}`}>
			<Line data={chartData} options={options} />
		</div>
	)
}
